/** One recorded share-price sample for a vault. */
export interface VaultYieldSnapshot {
  /** Unix seconds when the sample was taken. */
  t: number
  /** Raw `convertToAssets(1e18)` share price at `t`, as a string. */
  p: string
}

/** Per-vault time series, keyed by lowercased address. */
export type VaultYieldSeries = { [address: string]: VaultYieldSnapshot[] }

export interface VaultAprResult {
  /** Annualised realised return from share-price growth (e.g. 0.043 = 4.3%). */
  apr: number
  /** Share price at the latest sample. */
  sharePriceNow: string
  /** Share price at the lookback sample used. */
  sharePriceThen: string
  /** Seconds between the two samples used. */
  windowSeconds: number
  /** Number of samples in the series. */
  samples: number
}

const YEAR_SECONDS = 365 * 24 * 60 * 60
const SCALE = 10n ** 18n

export interface AppendSnapshotOptions {
  /** Max ring length — oldest samples drop off. Default 90. */
  maxPoints?: number
  /** Skip the append if the last sample is newer than this. Default 0
   *  (always append). Gates the ring to a sane cadence regardless of how
   *  often the recorder fires. */
  minIntervalSeconds?: number
}

/**
 * Append a sample to a vault's ring buffer, dropping the oldest beyond
 * `maxPoints` and skipping samples that arrive sooner than
 * `minIntervalSeconds` after the last one. Pure — returns a new array.
 */
export const appendSnapshot = (
  points: VaultYieldSnapshot[],
  snap: VaultYieldSnapshot,
  options?: AppendSnapshotOptions,
): VaultYieldSnapshot[] => {
  const maxPoints = options?.maxPoints ?? 90
  const minInterval = options?.minIntervalSeconds ?? 0
  const last = points[points.length - 1]
  if (last && snap.t - last.t < minInterval) return points
  const next = [...points, snap]
  return next.length > maxPoints ? next.slice(next.length - maxPoints) : next
}

export interface ComputeVaultAprOptions {
  /** Target lookback window. Default 7. The sample closest to
   *  `now − windowDays` (and strictly before `now`) is used as the base. */
  windowDays?: number
  /** Require at least this much elapsed between the two samples, else
   *  return `undefined` (a window too short makes the annualised figure
   *  meaningless). Default 1 hour. */
  minWindowSeconds?: number
}

/**
 * Annualise a vault's realised return from its recorded share-price
 * series:
 *
 *   apr = (priceNow / priceThen − 1) × (yearSeconds / elapsedSeconds)
 *
 * `priceNow` is the latest sample; `priceThen` is the sample closest to
 * `now − windowDays`. The ratio is computed in bigint (scaled by 1e18)
 * to preserve the small per-day growth that double precision would lose
 * on ~1e18-scale prices.
 *
 * Returns `undefined` when there aren't two usable samples or the window
 * is shorter than `minWindowSeconds`.
 */
export const computeVaultApr = (
  points: VaultYieldSnapshot[],
  options?: ComputeVaultAprOptions,
): VaultAprResult | undefined => {
  if (!points || points.length < 2) return undefined
  const windowDays = options?.windowDays ?? 7
  const minWindow = options?.minWindowSeconds ?? 3600

  const sorted = [...points].sort((a, b) => a.t - b.t)
  const now = sorted[sorted.length - 1]
  const target = now.t - windowDays * 86400

  // Base sample: the one strictly before `now` whose time is closest to
  // the target lookback. Falls back to the oldest sample.
  let then = sorted[0]
  for (const pt of sorted) {
    if (pt.t >= now.t) continue
    if (Math.abs(pt.t - target) < Math.abs(then.t - target)) then = pt
  }
  if (then.t >= now.t) return undefined

  const windowSeconds = now.t - then.t
  if (windowSeconds < minWindow) return undefined

  const pThen = BigInt(then.p)
  if (pThen === 0n) return undefined
  // ratioScaled ≈ 1e18 × (priceNow / priceThen) — bigint division keeps
  // the small growth that Number(p1)/Number(p0) would round away.
  const ratioScaled = (BigInt(now.p) * SCALE) / pThen
  const ratio = Number(ratioScaled) / 1e18
  const apr = (ratio - 1) * (YEAR_SECONDS / windowSeconds)

  return {
    apr,
    sharePriceNow: now.p,
    sharePriceThen: then.p,
    windowSeconds,
    samples: points.length,
  }
}
